// "A newer ClassMori is live" (2026-10-07): a teacher who keeps the app open from before a deploy keeps
// running the old build (new features never appear until a reload). The page compares the entry script
// it runs with the one the server now hands out (the 3 KB home page, ~1.5 KB gzipped), on return to the tab and every 3 minutes while it is visible, and offers
// a reload. It never reloads by itself, so nothing in progress (a lesson, a form) is lost.
import { useEffect, useState } from "react";
import { t } from "./i18n";

/** The hashed Vite entry in an HTML page, e.g. /assets/index-U0cfuzgw.js. */
export const entryFromHtml = (html: string) => html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/)?.[0] ?? null;

/** The entry this page is running; null in development (Vite serves /src/main.tsx). */
const runningEntry = () => {
  for (const script of document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')) {
    const path = new URL(script.src, location.href).pathname;
    if (/^\/assets\/index-[A-Za-z0-9_-]+\.js$/.test(path)) return path;
  }
  return null;
};

const CHECK_EVERY_MS = 3 * 60_000;
const MIN_GAP_MS = 60_000;

// Screens that must not be interrupted by a reload (customer batch §8): a page with something unsaved or a child-facing mode
// registers itself here while it is in that state. Projection and open dialogs are read from the page itself.
// D-255: "unsaved" (a save on its way: the contact book while typing, a score, attendance) comes and goes every second,
// so it only greys the reload button; hiding the banner for it made the page jump up and down under the teacher's typing.
// "screen" (a child-facing mode) hides the banner, like projection and dialogs.
type BlockKind = "unsaved" | "screen";
const blockers = new Map<symbol, BlockKind>();
export function useUpdateBlocker(active: boolean, reason: string, kind: BlockKind = "unsaved") {
  useEffect(() => {
    if (!active) return;
    const key = Symbol(reason);
    blockers.set(key, kind);
    return () => { blockers.delete(key); };
  }, [active, reason, kind]);
}
const BUSY_SELECTOR = '[role="dialog"], [role="alertdialog"], dialog[open], .tool-projecting, .classroom-projection, .individual-projection';
const screenBusy = () => [...blockers.values()].includes("screen") || (typeof document !== "undefined" && Boolean(document.querySelector(BUSY_SELECTOR)));
/** True while a reload would interrupt something; checked again when the button is clicked. */
export const updateBlocked = () => blockers.size > 0 || screenBusy();
const typingNow = () => { const el = typeof document !== "undefined" ? document.activeElement as HTMLElement | null : null; return !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable); };

/**
 * D-255: whether the banner is in the page. It first appears only at a calm moment (nothing unsaved, no field being typed
 * in, no child-facing screen), so it never pushes the page while the teacher types; once in, it stays — only a
 * child-facing screen hides it — and unsaved work just disables the button until it is saved.
 */
export function bannerState(shown: boolean, now: { available: boolean; screen: boolean; unsaved: boolean; typing: boolean }) {
  const next = shown || (now.available && !now.screen && !now.unsaved && !now.typing);
  return { shown: next, visible: next && !now.screen, canReload: !now.screen && !now.unsaved };
}

export function NewVersionBanner({ disabled = false }: { disabled?: boolean }) {
  const [available, setAvailable] = useState(false);
  const [state, setState] = useState({ shown: false, visible: false, canReload: false });
  useEffect(() => {
    if (!available) return;
    const sync = () => setState((old) => {
      const next = bannerState(old.shown, { available: true, screen: screenBusy(), unsaved: [...blockers.values()].includes("unsaved"), typing: typingNow() });
      return next.shown === old.shown && next.visible === old.visible && next.canReload === old.canReload ? old : next;
    });
    sync();
    const timer = window.setInterval(sync, 1500);
    return () => window.clearInterval(timer);
  }, [available]);
  useEffect(() => {
    const current = runningEntry();
    if (disabled || !current) return;
    let last = 0, stopped = false;
    const check = async () => {
      // A hidden tab does not look (D-153); coming back to it triggers the check through visibilitychange.
      if (stopped || document.visibilityState === "hidden" || Date.now() - last < MIN_GAP_MS) return;
      last = Date.now();
      try {
        const response = await fetch("/", { cache: "no-store", credentials: "same-origin" });
        if (!response.ok) return;
        const latest = entryFromHtml(await response.text());
        if (!stopped && latest && latest !== current) { stopped = true; setAvailable(true); }
      } catch { /* offline: try again later */ }
    };
    const timer = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVisible);
    // A move to another page of the app is a natural moment to look (the 60 s floor above still applies).
    addEventListener("popstate", onVisible);
    addEventListener("classmori-navigate", onVisible);
    return () => { stopped = true; window.clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); removeEventListener("popstate", onVisible); removeEventListener("classmori-navigate", onVisible); };
  }, [disabled]);
  if (!available || disabled || !state.visible) return null;
  return <div className="plan-banner update-banner" role="status">
    <span><strong>{t("小森課堂已更新")}</strong>{t("重新整理頁面，就能使用最新功能。")}</span>
    {/* aria-disabled, not disabled: the button keeps keyboard focus, so the reason in its title can still be read. */}
    <button type="button" className="button primary" aria-disabled={!state.canReload || undefined} title={state.canReload ? undefined : t("正在儲存，存好後就能重新整理。")} onClick={() => { if (state.canReload && !updateBlocked()) location.reload(); }}>{t("重新整理")}</button>
  </div>;
}
